import { Icon, type IconProps } from "@design-system/Icon";
import SearchBar from "@design-system/Inputs/SearchBar";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text, type TextProps } from "@design-system/Typography/Text";
import { type ClassValue, cn } from "@design-system/Utilities";
import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu";
import React, {
    createContext,
    type FC,
    Fragment,
    type HTMLAttributes,
    type PointerEvent,
    type PropsWithChildren,
    type ReactNode,
    type Ref,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Chip, type ChipProps } from "../Chip";
import { useDropdownItemStyles } from "../UniversalDropdown";
import {
    useDropdownContentStyles,
    useDropdownItemIconStyles,
    useDropdownItemTextStyles,
} from "../UniversalDropdown/styles";

type MultiSelectContext = {
    replaceTrigger?: boolean;
    isOpen: boolean;
    onValuesChange: (values: (string | null)[]) => void;
    values: (string | null)[];
    onReset?: () => void;
};

type MultiSelectComponent = FC<
    PropsWithChildren<
        Omit<MultiSelectContext, "isOpen"> & DropdownPrimitive.DropdownMenuProps
    >
> & {
    Trigger: typeof Trigger;
    Content: typeof MultiSelectContent;
    Search: typeof Search;
    Group: typeof Group;
    ItemIcon: typeof ItemIcon;
    ItemText: typeof ItemText;
    Item: typeof Item;
    Label: typeof Label;
    Sub: typeof Sub;
    SubTrigger: typeof SubTrigger;
    SubContent: typeof SubContent;
    Reset: typeof Reset;
    ChipTrigger: typeof ChipTrigger;
};

export const MultiSelectContext = createContext<MultiSelectContext | null>(
    null,
);

interface TriggerProps
    extends HTMLAttributes<HTMLButtonElement>,
        PropsWithChildren {
    ref?: Ref<HTMLButtonElement>;
}

const Trigger: FC<TriggerProps> = ({ ref, ...props }) => {
    const ctx = useContext(MultiSelectContext);
    if (!ctx) throw new Error("Context must not be null");
    const { replaceTrigger, isOpen } = ctx;

    const hideTrigger = replaceTrigger && isOpen;
    const triggerStyleProps = hideTrigger
        ? {
              style: {
                  height: 0,
                  width: 0,
                  padding: 0,
                  border: 0,
                  minHeight: 0,
                  minWidth: 0,
                  display: "block",
                  overflow: "hidden",
              },
          }
        : {};

    const Wrapper = replaceTrigger ? "div" : Fragment;

    return (
        <Wrapper>
            {/**
             * We need to have a trigger because the content position
             * will be calculated based on the trigger position and size.
             * We couldn't find a way for the content to be displayed over
             * the trigger, so we need a non dimentional, but still present,
             * trigger.
             */}
            <DropdownPrimitive.Trigger {...triggerStyleProps} asChild ref={ref}>
                {/**
                 * We need to keep the children elements here because removing
                 * them causes the content to lose its anchor, which results in
                 * a flash or flicker on the screen for a brief moment.
                 */}
                {props.children}
            </DropdownPrimitive.Trigger>
            {/**
             * Nonetheless, we need to make sure that the space previously
             * occupied by the trigger remains occupied (the content in
             * fact is in a portal, it doesn't occupy the space of the
             * trigger). So we still render the trigger's children, but
             * outside it, so it doesn't afct the content position.
             *
             */}

            {hideTrigger && <Box className="invisible">{props.children}</Box>}
        </Wrapper>
    );
};

const Label: FC<{
    value: string | null;
    subItemsValues: (string | null)[];
    children: (props: { isGroupSelected: boolean | "partial" }) => ReactNode;
}> = (props) => {
    const ctx = useContext(MultiSelectContext);
    if (!ctx) throw new Error("Context must not be null");
    const { values, onValuesChange } = ctx;
    const isGroupSelected = props.subItemsValues.every((elt) =>
        values.includes(elt),
    )
        ? true
        : props.subItemsValues.every((elt) => !values.includes(elt))
          ? false
          : "partial";

    const toggleActive = () => {
        if (isGroupSelected === true) {
            onValuesChange(
                values.filter((elt) => !props.subItemsValues.includes(elt)),
            );
            return;
        }

        onValuesChange([...values, ...props.subItemsValues]);
    };

    const onClick = (e: React.MouseEvent) => {
        e.preventDefault();
        toggleActive();
    };
    const className = useDropdownItemStyles({});

    return (
        <div onClick={onClick} className={cn(className)}>
            {props.children({ isGroupSelected })}
        </div>
    );
};

const Group: FC<PropsWithChildren<{ className?: ClassValue }>> = (props) => {
    return (
        <DropdownPrimitive.Group className="cursor-pointer">
            {props.children}
        </DropdownPrimitive.Group>
    );
};

const Sub: FC<PropsWithChildren> = (props) => {
    return <DropdownPrimitive.Sub>{props.children}</DropdownPrimitive.Sub>;
};

const SubTrigger: FC<PropsWithChildren> = (props) => {
    return (
        <DropdownPrimitive.SubTrigger asChild>
            {props.children}
        </DropdownPrimitive.SubTrigger>
    );
};

const SubContent: FC<PropsWithChildren<{ className?: ClassValue }>> = ({
    className,
    children,
    ...props
}) => {
    return (
        <DropdownPrimitive.Portal>
            <DropdownPrimitive.SubContent sideOffset={10}>
                <FlexCol
                    {...props}
                    p="1"
                    elevation="dropdown"
                    br="xl"
                    className={cn(
                        "w-[220px] max-h-[300px] border bg-white overflow-y-auto scrollbar-hide",
                        className,
                    )}
                >
                    {children}
                </FlexCol>
            </DropdownPrimitive.SubContent>
        </DropdownPrimitive.Portal>
    );
};

const Item: FC<{
    value: string | null;
    children: ReactNode | ((props: { isSelected: boolean }) => ReactNode);
    variant?: "negative";
}> = (props) => {
    const ctx = useContext(MultiSelectContext);
    if (!ctx) throw new Error("Context must not null");
    const { values, onValuesChange } = ctx;
    const isSelected = values.includes(props.value);
    const toggleActive = () => {
        if (isSelected) {
            onValuesChange(values.filter((elt) => elt !== props.value));
        } else {
            onValuesChange([...values, props.value]);
        }
    };

    const onClick = (e: React.MouseEvent) => {
        e.preventDefault();
        toggleActive();
    };

    const children =
        typeof props.children === "function"
            ? props.children({ isSelected })
            : props.children;

    const itemClassName = useDropdownItemStyles({
        active: isSelected,
        negative: props.variant === "negative",
    });

    return (
        <DropdownPrimitive.Item
            onClick={onClick}
            className={cn(itemClassName, "cursor-pointer outline-none")}
        >
            {children}
        </DropdownPrimitive.Item>
    );
};

interface ItemTextProps extends TextProps {
    ref?: Ref<HTMLDivElement>;
}

const ItemText: FC<ItemTextProps> = ({
    className: propsClassName,
    ref,
    ...props
}) => {
    const className = useDropdownItemTextStyles(propsClassName);
    return <Text ref={ref} {...props} className={className} />;
};

interface ItemIconProps extends IconProps {
    ref?: Ref<SVGSVGElement>;
}

const ItemIcon: FC<ItemIconProps> = ({
    className: propsClassName,
    ref,
    ...props
}) => {
    const className = useDropdownItemIconStyles(propsClassName);
    return <Icon {...props} className={className} ref={ref} />;
};

const Search: FC<
    PropsWithChildren<{
        searchString: string | undefined;
        setSearchString: (value: string | undefined) => void;
        placeholder?: string;
        className?: ClassValue;
    }>
> = (props) => {
    return <SearchBar {...props} />;
};

const MultiSelectContent: FC<
    PropsWithChildren<{
        avoidCollisions?: boolean;
        className?: ClassValue;
        align?: DropdownPrimitive.DropdownMenuContentProps["align"];
        alignOffset?: DropdownPrimitive.DropdownMenuContentProps["alignOffset"];
        content?: string;
        hasPortal?: boolean;
        side?: DropdownPrimitive.DropdownMenuContentProps["side"];
        sideOffset?: DropdownPrimitive.DropdownMenuContentProps["sideOffset"];
    }>
> = ({
    avoidCollisions,
    align = "start",
    alignOffset = 0,
    hasPortal = true,
    side = "bottom",
    sideOffset = 4,
    children,
    className,
    ...props
}) => {
    const ctx = useContext(MultiSelectContext);
    if (!ctx) throw new Error("Context must not be null");
    const { replaceTrigger } = ctx;
    const Wrapper = hasPortal ? DropdownPrimitive.Portal : Fragment;
    const contentClassName = useDropdownContentStyles();
    return (
        <Wrapper>
            <DropdownPrimitive.Content
                {...props}
                align={replaceTrigger ? "start" : align}
                alignOffset={replaceTrigger ? 0 : alignOffset}
                side={replaceTrigger ? "bottom" : side}
                sideOffset={replaceTrigger ? 0 : sideOffset}
                avoidCollisions={avoidCollisions || false}
            >
                <FlexCol
                    p="1"
                    elevation="dropdown"
                    br="xl"
                    className={cn(contentClassName, className)}
                >
                    {children}
                </FlexCol>
            </DropdownPrimitive.Content>
        </Wrapper>
    );
};

const Reset: FC<HTMLAttributes<HTMLDivElement> & { label: string }> = ({
    className,
    label,
}) => {
    const itemClassName = useDropdownItemStyles({}, className);
    const ctx = useContext(MultiSelectContext);

    return (
        <FlexRow onClick={ctx?.onReset} className={itemClassName}>
            <Icon name="close" size="sm" />
            <Text>{label}</Text>
        </FlexRow>
    );
};

type MultiSelectChipTriggerProps<O extends Option> = TriggerProps &
    Omit<ChipProps, "active"> & {
        /**
         * The label to display in the chip.
         *
         * @example
         * label = "Options"
         * Then the chip will display "Options" when nothing is selected,
         * and "Options: Option 1, +1" when an option is selected.
         * The colon is automatically added when some values are selected.
         */
        label?: string;
        /**
         * The options to display in the dropdown.
         * They are used to extract the value to display in the chip based
         * on the selected values of the dropdown.
         *
         * @example
         * options = [{ id: "1", name: "Option 1" }, { id: "2", name: "Option 2" }]
         */
        options?: O[];
        /**
         * The name of the property on each option object that holds its unique identifier.
         * This identifier is used to match the selected value(s) from the Dropdown context
         * with the corresponding object in the `options` array. The value accessed via this key
         * (e.g., `option[valueIdentifierKey]`) should be a string and match the type of values stored
         * in the Dropdown's selection state.
         *
         * @default "id"
         * @example valueIdentifierKey="productId"
         * @example // Given options: [{ value: 'abc', label: 'Item 1' }]
         * valueIdentifierKey="value"
         */
        valueIdentifierKey?: keyof O;

        /**
         * The name of the property on each option object that holds the human-readable text label.
         * Once an option is matched using `valueIdentifierKey`, this key is used to retrieve
         * the string that should be displayed in the chip for that selected option
         * (e.g., `matchedOption[valueDisplayKey]`).
         *
         * @default "name"
         * @example valueDisplayKey="title"
         * @example // Given options: [{ id: 'abc', description: 'Item 1' }]
         * valueDisplayKey="description"
         */
        valueDisplayKey?: keyof O;
    };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Option = Record<string, any>;
function useValueDisplay<O extends Option>(
    value: (string | null)[] | undefined,
    options: O[] | undefined,
    valueIdentifierKey: keyof O | undefined,
    valueDisplayKey: keyof O | undefined,
): { mainValue: string | null; additionalValuesNumber: number } {
    if (value?.length) {
        const mainValueId = value[0];
        const correspondingOption = options?.find(
            (option) => option[valueIdentifierKey ?? "id"] === mainValueId,
        );
        if (!correspondingOption) {
            console.warn(`Option with id ${mainValueId} not found in options`);
        }
        const mainValue =
            correspondingOption?.[valueDisplayKey ?? "name"] ??
            correspondingOption?.["id"] ??
            mainValueId;

        if (mainValue !== undefined && typeof mainValue !== "string") {
            throw new Error("Main value must be a string");
        }

        const additionalValuesNumber = value.length - 1;
        return { mainValue, additionalValuesNumber };
    }
    return { mainValue: null, additionalValuesNumber: 0 };
}
function useNoOptionsWarning(options: Option[] | undefined) {
    useEffect(() => {
        if (!options) {
            console.warn(
                "You are not providing options to the Dropdown Trigger component," +
                    "so the value displayed in the trigger may not be the same as the actual label in the dropdown.",
            );
        }
    }, [options]);
}
const ChipTrigger = <O extends Option>({
    label,
    valueDisplayKey,
    valueIdentifierKey,
    options,
    ref,
    ...props
}: MultiSelectChipTriggerProps<O>) => {
    const ctx = useContext(MultiSelectContext);

    if (!ctx) {
        throw new Error(
            "MultiSelectChipTrigger must be used within a MultiSelect",
        );
    }

    const { mainValue, additionalValuesNumber } = useValueDisplay(
        ctx.values,
        options,
        valueIdentifierKey,
        valueDisplayKey,
    );

    const displayedValue = useMemo(() => {
        if (!mainValue) {
            return undefined;
        }
        if (additionalValuesNumber === 0) {
            return mainValue;
        }
        return `${mainValue}, +${additionalValuesNumber}`;
    }, [mainValue, additionalValuesNumber]);

    const handleClick = (e: PointerEvent<SVGSVGElement>) => {
        if (!ctx.values?.length) return;
        e.stopPropagation();
        e.preventDefault();
        ctx.onReset?.();
    };

    useNoOptionsWarning(options);

    return (
        <DropdownPrimitive.Trigger {...props} asChild ref={ref}>
            <Chip active={!!ctx.values?.length}>
                <Chip.Label>{label}</Chip.Label>
                {!!displayedValue && <Chip.Value>{displayedValue}</Chip.Value>}
                <Chip.Icon
                    name={ctx.values?.length ? "close" : "chevronDown"}
                    onPointerDown={handleClick}
                />
            </Chip>
        </DropdownPrimitive.Trigger>
    );
};

export const MultiSelect: MultiSelectComponent = ({
    children,
    replaceTrigger = false,
    onOpenChange,
    onValuesChange,
    values,
    ...props
}) => {
    const [selectOpen, setSelectOpen] = useState(false);
    const handleOpenChange = (open: boolean) => {
        onOpenChange?.(open);
        setSelectOpen(open);
    };
    const onReset = () => {
        if (props.onReset) {
            props.onReset();
        } else {
            onValuesChange([]);
        }
    };

    return (
        <MultiSelectContext.Provider
            value={{
                replaceTrigger,
                isOpen: selectOpen,
                onValuesChange,
                values,
                onReset,
            }}
        >
            <DropdownPrimitive.Root
                open={selectOpen}
                onOpenChange={handleOpenChange}
                {...props}
            >
                {children}
            </DropdownPrimitive.Root>
        </MultiSelectContext.Provider>
    );
};

MultiSelect.Trigger = Trigger;
MultiSelect.Content = MultiSelectContent;
MultiSelect.Item = Item;
MultiSelect.ItemIcon = ItemIcon;
MultiSelect.ItemText = ItemText;
MultiSelect.Search = Search;
MultiSelect.Label = Label;
MultiSelect.Group = Group;
MultiSelect.Sub = Sub;
MultiSelect.SubTrigger = SubTrigger;
MultiSelect.SubContent = SubContent;
MultiSelect.Reset = Reset;
MultiSelect.ChipTrigger = ChipTrigger;
